import { Button } from '@/components/common/Button/Button';
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogContent,
  DialogBody,
  DialogFooter,
} from '@/components/common/Dialog/Dialog';
import { RadioButton, RadioGroup } from '@/components/common/RadioGroup/RadioGroup';
import { TextButton } from '@/components/common/TextButton/TextButton';
import { AREA_TO_PREFECTURES, PREFECTURE_AREA_KEYS } from '@/constants/prefectures';
import { useTranslations } from 'next-intl';

interface SelectPrefectureDialogProps {
  isOpen: boolean;
  isEnableClear: boolean;
  value: string | undefined;
  onOpenChange: (isOpen: boolean) => void;
  onValueChange: (value: string) => void;
  onClickClear: () => void;
}

export function SelectPrefectureDialog({
  isOpen = false,
  isEnableClear = false,
  value,
  onValueChange,
  onClickClear,
  onOpenChange,
}: SelectPrefectureDialogProps) {
  const t = useTranslations('common');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('prefectureSelectDialog.title')}</DialogTitle>
          <TextButton fontWeight="bold" disabled={!isEnableClear} onClick={onClickClear}>
            {t('prefectureSelectDialog.clear')}
          </TextButton>
        </DialogHeader>
        <DialogBody>
          <RadioGroup onValueChange={onValueChange} value={value} className="flex flex-col gap-6">
            {PREFECTURE_AREA_KEYS.map((area) => (
              <div key={area} className="flex flex-col gap-2">
                <h3 className="text-x-small font-bold text-neutral-content">
                  {t(`prefecture.area.${area}`)}
                </h3>
                <div className="grid grid-cols-2 gap-x-3 tab:grid-cols-4 pc:grid-cols-4">
                  {AREA_TO_PREFECTURES[area].map((prefecture, index) => {
                    const totalItems = AREA_TO_PREFECTURES[area].length;

                    // For 2 columns (mobile): check if item is in last row
                    const rowsMobile = Math.ceil(totalItems / 2);
                    const isInLastRowMobile = index >= (rowsMobile - 1) * 2;

                    // For 4 columns (tablet/PC): check if item is in last row
                    const rowsDesktop = Math.ceil(totalItems / 4);
                    const isInLastRowDesktop = index >= (rowsDesktop - 1) * 4;

                    // Mobile border class
                    const mobileBorderClass = !isInLastRowMobile
                      ? 'border-b-thin border-neutral-auxiliary'
                      : '';

                    // Desktop border classes - completely separate logic
                    const desktopBorderClass = !isInLastRowDesktop
                      ? 'tab:border-b-thin tab:border-neutral-auxiliary pc:border-b-thin pc:border-neutral-auxiliary'
                      : 'tab:border-b-[0px] pc:border-b-[0px]';

                    return (
                      <div
                        key={prefecture}
                        className={`py-3 gap-3 ${mobileBorderClass} ${desktopBorderClass}`}
                      >
                        <RadioButton
                          id={prefecture}
                          value={prefecture}
                          label={t(`prefecture.label.${prefecture}`)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </RadioGroup>
        </DialogBody>
        <DialogFooter>
          <Button colorScheme="secondary" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
